import ApiServices, { InstanceKeys } from "@/api/apexchat.api.services";
import { LeadAPI } from "@/api/endpoints";
import { cloneDeep, getParsedJson } from "@/services/generic.methods";
import storage, {
  getItemFromParsedStorage,
  getParsedStorage,
} from "@/services/storage";
import { createSlice } from "@reduxjs/toolkit";

const getRecordsPerPage = (records) => {
  const mappedRecords = {};
  const storage = getParsedStorage("PageLimits");
  for (let key in records) {
    mappedRecords[key] = storage?.[key] || records?.[key] || 10;
  }
  return mappedRecords;
};

export const crmLeadSlice = createSlice({
  name: "crmLeads",
  initialState: {
    lead: null,
    leadById: {},
    leadColumns: [],
    dropdownOptions: {},
    recordsPerPage: getRecordsPerPage({
      leads: 10,
      import: 10,
    }),
    leadGridSettings: {
      columns: null,
      columnFilters: null,
      sortingInfo: null,
    },
    customCodes: [],
    leadWorkflows: [],
    searchFields: [],
    smsSearchFields: [],
    emailSearchFields: [],
    appointmentSearchFields: [],
    leadUpdateFields: [],
    leadScore: {},
  },

  reducers: {
    setLeadToState: (state, action) => {
      state.lead = action.payload;
    },
    setCustomCodesToState: (state, action) => {
      state.customCodes = action.payload;
    },
    setLeadToStateById: (state, action) => {
      const lead = action.payload;
      if (lead?.id) {
        state.leadById[lead.id] = lead;
      }
    },
    setRemoveLeadFromStateById: (state, action) => {
      const lead = action.payload;
      if (lead?.id) {
        const leads = cloneDeep(state.leadById);
        if (leads?.[lead.id]) {
          delete leads[lead.id];
        }
        state.leadById = leads;
      }
    },
    setLeadAttachments: (state, action) => {
      const { leadId, attachments } = action.payload;
      state.leadById[leadId].leadAttachments = [...attachments];
    },
    setLeadColumnsToState: (state, action) => {
      state.leadColumns = action.payload;
    },
    setLeadGridSettingsToState: (state, action) => {
      state.leadGridSettings = action.payload;
    },
    setLeadWorkflowsToState: (state, action) => {
      state.leadWorkflows = action.payload;
    },

    /**
     * @name @setRecordsPerPage
     * @description to keep the leads count per page
     **/
    setRecordsPerPage: (state, action) => {
      storage.updateStorageItem("PageLimits", action.payload);
      const recordsPerPage = JSON.parse(JSON.stringify(state.recordsPerPage));
      const payload = {
        ...recordsPerPage,
        ...action.payload,
      };
      state.recordsPerPage = payload;
    },
    setLeadsSearchFields: (state, action) => {
      state.searchFields = action.payload;
    },
    setSMSSearchFields: (state, action) => {
      state.smsSearchFields = action.payload;
    },
    setEmailSearchFields: (state, action) => {
      state.emailSearchFields = action.payload;
    },
    setAppointmentSearchFields: (state, action) => {
      state.appointmentSearchFields = action.payload;
    },
    setLeadUpdateFields: (state, action) => {
      state.leadUpdateFields = action.payload;
    },
    setLeadScoringDetailsToState: (state, action) => {
      const data = action.payload || {};
      if (data) {
        data.totalScore = data?.items?.reduce(
          (acc, section) => acc + section?.leadScoringRule?.score || 0,
          0
        );
        state.leadScore = data;
      }
    },
  },
});

export const {
  setLeadToState,
  setLeadToStateById,
  setRemoveLeadFromStateById,
  setLeadColumnsToState,
  setLeadGridSettingsToState,
  setLeadAttachments,
  setRecordsPerPage,
  setLeadWorkflowsToState,
  setCustomCodesToState,
  setLeadsSearchFields,
  setSMSSearchFields,
  setEmailSearchFields,
  setAppointmentSearchFields,
  setLeadUpdateFields,
  setLeadScoringDetailsToState,
} = crmLeadSlice.actions;

export const selectLead = (state) => {
  return state.crmLeads;
};
export const getCustomCodes = (state) => {
  return state.crmLeads?.customCodes;
};

export const getLeadInfo = (state) => {
  return state.crmLeads.lead;
};
export const getLeadInfoById = (state) => {
  return state.crmLeads?.leadById;
};
export const getLeadColumns = (state) => {
  return state.crmLeads.leadColumns;
};
export const getLeadGridSettings = (state) => {
  return state.crmLeads.leadGridSettings;
};

export const uploadLeadAttachment = (payload, params, httpConfig) => {
  let url = `${LeadAPI.uploadAttachment}&${params}`;
  return ApiServices.postAttachements(
    url,
    payload,
    InstanceKeys.crmInstance,
    httpConfig
  );
};

export const selectLeadsRecordsCount = (state) => {
  return state.crmLeads?.recordsPerPage;
};

export const selectLeadWorkflows = (state) => {
  return state.crmLeads?.leadWorkflows;
};
export const selectLeadSeachFields = (state) => {
  return state.crmLeads?.searchFields;
};
export const selectSMSSeachFields = (state) => {
  return state.crmLeads?.smsSearchFields;
};
export const selectEmailSeachFields = (state) => {
  return state.crmLeads?.emailSearchFields;
};
export const selectAppointmentSeachFields = (state) => {
  return state.crmLeads?.appointmentSearchFields;
};
export const selectLeadUpdateFields = (state) => {
  return state.crmLeads?.leadUpdateFields;
};
export const selectLeadScoreDetails = (state) => {
  return state.crmLeads?.leadScore;
};

export default crmLeadSlice.reducer;
